import { type DropDownProps, Space } from "antd";
import { Fragment } from "react";

import { BTDropdown } from "@btnet/core-components";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTIconEllipsisOutlined } from "commonComponents/btWrappers/BTIcon";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { type EditorControlBreakpointNames } from "commonComponents/btWrappers/editor/core/EditorControlBar/EditorControlBar.types";
import { type ExtensionName } from "commonComponents/btWrappers/editor/core/extensions/editorExtensionUtils";
import { type IEditor } from "commonComponents/btWrappers/editor/core/useBTEditor.types";

export interface ICollapsableControl {
    /**
     * The JSX element representing the control if the control were to be collapsed in a dropdown menu
     * for when the editor is displayed in small containers.
     */
    collapsedMenuItem: (editor: IEditor, supportedExtensions: Set<ExtensionName>) => JSX.Element;
    /**
     *  Used to mark the "menu items" as active when the control is in "collapsed" mode
     */
    collapsedActiveKeys: (editor: IEditor) => string[];
    /**
     * The JSX element representing the control if the control were in it's "expanded" form (ie. not in a
     * dropdown submenu). Typically this is just a button to trigger the control.
     */
    expandedElement: (
        editor: IEditor,
        supportedExtensions: Set<ExtensionName>,
        dropdownPlacement: DropDownProps["placement"],
    ) => JSX.Element;
    /**
     * Given the list of extensions supported by the current editor configuration, is the control
     * even supported at all? If it's not, it will be hidden
     */
    isControlSupported: (supportedExtensions: Set<ExtensionName>, editor: IEditor) => boolean;
    /**
     * Given the current state of the editor, is the control allowed to be toggled? This determines whether or
     * not the control is disabled. If all options are disabled in the group, the group is disabled
     */
    isControlEnabled: (editor: IEditor) => boolean;
    key: string;
}

interface ICollapsableControlGroupProps
    extends Record<Exclude<EditorControlBreakpointNames, "inf">, number> {
    "data-testid": string;
    controls: ICollapsableControl[];
    editor: IEditor;
    supportedExtensions: Set<ExtensionName>;
    editorSize: EditorControlBreakpointNames;
    collapseIcon?: React.ReactNode;
    dropdownPlacement?: DropDownProps["placement"];
    gap?: number;
}

const defaultCollapseIcon = <BTIconEllipsisOutlined />;

export const CollapsableControlGroup: React.FC<ICollapsableControlGroupProps> = ({
    "data-testid": dataTestId,
    controls,
    editor,
    supportedExtensions,
    editorSize,
    collapseIcon = defaultCollapseIcon,
    dropdownPlacement,
    gap,
    ...otherProps
}) => {
    const supportedControls = controls.filter((control) =>
        control.isControlSupported(supportedExtensions, editor),
    );

    const numberOfExpandedControls =
        editorSize === "inf" ? controls.length : otherProps[editorSize];

    const expandedControls = supportedControls.slice(0, numberOfExpandedControls);
    const collapsedControls = supportedControls.slice(numberOfExpandedControls);

    // If there's only one collapsed control, might as well just expand it, otherwise we're collapsing for no reason
    if (collapsedControls.length === 1) {
        expandedControls.push(collapsedControls.pop()!);
    }

    const combinedControlActiveKeys = collapsedControls.flatMap((control) =>
        control.collapsedActiveKeys(editor),
    );
    const isSomeCollapsedControlToggled = combinedControlActiveKeys.length > 0;

    return (
        <Space size={gap ?? 2} data-testid={dataTestId}>
            {expandedControls.map((control) => (
                <Fragment key={control.key}>
                    {control.expandedElement(editor, supportedExtensions, dropdownPlacement)}
                </Fragment>
            ))}
            {collapsedControls.length > 0 && (
                <BTDropdown
                    testid={`${dataTestId}-collapsed-dropdown`}
                    overlay={
                        <BTMenu
                            testid={`${dataTestId}-collapsedMenu`}
                            selectedKeys={combinedControlActiveKeys}
                        >
                            {collapsedControls.map((control) => (
                                <Fragment key={control.key}>
                                    {control.collapsedMenuItem(editor, supportedExtensions)}
                                </Fragment>
                            ))}
                        </BTMenu>
                    }
                    placement={dropdownPlacement}
                >
                    <BTButtonDeprecated
                        data-testid={`${dataTestId}-collapsedTrigger`}
                        type={isSomeCollapsedControlToggled ? "primary" : "tertiary"}
                        icon={collapseIcon}
                        aria-pressed={isSomeCollapsedControlToggled}
                    />
                </BTDropdown>
            )}
        </Space>
    );
};
